import React from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Stack,
    Divider,
    Paper,
    Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import CampaignIcon from '@mui/icons-material/Campaign';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import useSEO from '../hooks/useSEO';

const CheestaServicesPage: React.FC = () => {
    const navigate = useNavigate();
    useSEO({
        title: 'خدمات چیستا',
        description: 'خدمات چیستا برای کسب‌وکارهای ایرانی: درج آگهی، تحلیل آمار، تبلیغات هدفمند و پشتیبانی.',
        canonical: '/cheesta-services',
    });

    const services = [
        {
            title: 'درج و مدیریت آگهی',
            titleEn: 'Directory Listing',
            desc: 'ثبت رایگان کسب و کار در جامع‌ترین دایرکتوری ایرانیان استرالیا با قابلیت مدیریت کامل محتوا، تصاویر و اطلاعات تماس.',
            icon: <BusinessIcon sx={{ fontSize: 40 }} />,
            color: '#2854a1',
            bg: '#eff6ff'
        },
        {
            title: 'تحلیل آمار و عملکرد',
            titleEn: 'Performance Analytics',
            desc: 'دسترسی به پنل پیشرفته برای مشاهده دقیق آمار بازدیدها، کلیک‌ها و رفتار مشتریان برای اتخاذ تصمیمات بهتر.',
            icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
            color: '#0891b2',
            bg: '#ecfeff'
        },
        {
            title: 'بهینه‌سازی سئو (SEO)',
            titleEn: 'SEO Optimization',
            desc: 'زیرساخت فنی چیستا به گونه‌ای طراحی شده که آگهی شما در نتایج جستجوی گوگل به بهترین شکل نمایش داده شود.',
            icon: <SearchIcon sx={{ fontSize: 40 }} />,
            color: '#059669',
            bg: '#f0fdf4'
        },
        {
            title: 'تبلیغات هدفمند',
            titleEn: 'Targeted Advertising',
            desc: 'نمایش آگهی شما در صفحات پربازدید و دسته‌بندی‌های مرتبط برای جذب حداکثری مشتریان بالقوه در استرالیا.',
            icon: <CampaignIcon sx={{ fontSize: 40 }} />,
            color: '#dc2626',
            bg: '#fef2f2'
        },
        {
            title: 'پشتیبانی ۲۴/۷',
            titleEn: 'Premium Support',
            desc: 'تیم پشتیبانی چیستا در تمام مراحل ثبت و ارتقای آگهی، همراه و راهنمای شما خواهد بود.',
            icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
            color: '#7c3aed',
            bg: '#f5f3ff'
        },
        {
            title: 'اسکن خودکار وب‌سایت',
            titleEn: 'Website Auto-Scan',
            desc: 'آدرس وب‌سایت خود را وارد کنید تا چیستا اطلاعات، تصاویر و ساعات کاری کسب و کار شما را به‌صورت خودکار بخواند و پیش‌نویس آگهی را برایتان آماده کند.',
            icon: <TravelExploreIcon sx={{ fontSize: 40 }} />,
            color: '#d97706',
            bg: '#fffbeb'
        }
    ];

    return (
        <Box sx={{ bgcolor: '#f8fafc', pb: 10 }}>
            {/* Hero Section */}
            <Box 
                sx={{ 
                    bgcolor: '#2854a1', 
                    color: 'white', 
                    py: { xs: 6, md: 8 }, 
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: { xs: 150, md: 220 },
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography 
                        variant="h1" 
                        fontWeight={900} 
                        sx={{ 
                            mb: 2, 
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                            textShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}
                    >
                        خدمات چیستا
                    </Typography>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            opacity: 0.9, 
                            direction: 'rtl', 
                            lineHeight: 1.8,
                            fontWeight: 400,
                            maxWidth: '700px',
                            mx: 'auto'
                        }}
                    >
                        ما ابزارهای لازم برای رشد و دیده شدن کسب و کار شما در جامعه ایرانیان استرالیا را فراهم کرده‌ایم
                    </Typography>
                </Container>
                {/* Decorative Elements */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
            </Box>

            <Container sx={{ mt: -6, position: 'relative', zIndex: 2 }}>
                <Grid container spacing={4}>
                    {services.map((service, index) => (
                        <Grid size={{ xs: 12, md: 4 }} key={index}>
                            <Card 
                                sx={{ 
                                    height: '100%', 
                                    borderRadius: 4, 
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                    <Avatar 
                                        sx={{ 
                                            width: 80, 
                                            height: 80, 
                                            mx: 'auto', 
                                            mb: 3, 
                                            bgcolor: service.bg, 
                                            color: service.color 
                                        }}
                                    >
                                        {service.icon}
                                    </Avatar>
                                    <Typography variant="h5" fontWeight={800} sx={{ mb: 1, color: '#1e293b' }}>
                                        {service.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: service.color, fontWeight: 700, mb: 2, display: 'block', letterSpacing: 1 }}>
                                        {service.titleEn}
                                    </Typography>
                                    <Divider sx={{ mb: 3, width: 40, mx: 'auto', borderColor: service.color, borderWidth: 2, borderRadius: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.8, direction: 'rtl' }}>
                                        {service.desc}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Call to Action Section */}
                <Paper 
                    elevation={0} 
                    sx={{ 
                        mt: 10, 
                        p: { xs: 4, md: 8 }, 
                        borderRadius: 8, 
                        bgcolor: 'white', 
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h4" fontWeight={800} sx={{ mb: 3, color: '#1e293b' }}>
                        آماده‌اید کسب و کار خود را ارتقا دهید؟
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', mb: 5, direction: 'rtl', maxWidth: '600px', mx: 'auto' }}>
                        همین امروز به جمع صدها کسب و کار ایرانی موفق در استرالیا بپیوندید و از تمام امکانات چیستا بهره‌مند شوید.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <Button 
                            variant="contained" 
                            size="large" 
                            onClick={() => navigate('/user/business/register')}
                            sx={{ 
                                bgcolor: '#2854a1', 
                                borderRadius: 3, 
                                px: 6, 
                                py: 1.5, 
                                fontWeight: 800,
                                '&:hover': { bgcolor: '#1e3d7a' }
                            }}
                        >
                            ثبت رایگان کسب و کار
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="large" 
                            onClick={() => navigate('/hire-us')}
                            sx={{ 
                                color: '#2854a1', 
                                borderColor: '#2854a1', 
                                borderRadius: 3, 
                                px: 6, 
                                py: 1.5, 
                                fontWeight: 800 
                            }}
                        >
                            {'\u2067دریافت مشاوره IT\u2069'}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default CheestaServicesPage;
